import React, { memo, useCallback, useMemo } from 'react';
import type { CSSProperties } from 'react';
import type { TapTarget, StepNode, LocalizedLabel, LocalizedImage, AnimationConfig, TapTargetIconPlacement } from '@segmently/schema-models/v2';
import { resolveLabel, type TemplateVariables } from '../../../utils/contentResolver';
import { mapTapTargetStyles, mapFrameLayoutStyles, mapTypographyStyles, mapEdgeInsetsToCSS } from '../../../utils/styleMapper';
import { useFontOverrides } from '@modules/font-config/context/FontConfigContext';
import { createButtonClickEvent } from '../../../utils/eventEmitter';
import { getButtonStateFromSchema } from '../../../utils/buttonVisibility';
import { getSchemaTestId } from '../../../utils/testIdUtils';
import { resolveScreenForegroundColor } from '../../../utils/canvasForeground';
import { useScreenAnimationContext, useTapTargetAnimation } from '../../../animations';
import type { OnActionCallback } from '../../../types/events';
import { Image } from '../Image';
import styles from './Button.module.css';

export interface ButtonProps {
  tapTarget: TapTarget | undefined;
  locale: string;
  variables?: TemplateVariables;
  step: StepNode;
  onAction?: OnActionCallback;
  disabled?: boolean;
  isLoading?: boolean;
  className?: string;
  'data-testid'?: string;
  editPath?: string;
}

function hasTranslations(content: unknown): content is { translations: Record<string, unknown> } {
  return (
    typeof content === 'object' &&
    content !== null &&
    'translations' in content &&
    typeof (content as { translations: unknown }).translations === 'object'
  );
}

/** Default animation durations per kind */
const ANIM_DEFAULTS: Record<string, { duration: number; repeat: boolean }> = {
  pulse:   { duration: 2,   repeat: true },
  shimmer: { duration: 3,   repeat: true },
  bounce:  { duration: 1.5, repeat: true },
  shake:   { duration: 0.8, repeat: true },
  glow:    { duration: 2,   repeat: true },
  ripple:  { duration: 1,   repeat: true },
  scale:   { duration: 0.5, repeat: false },
  fadeIn:  { duration: 0.5, repeat: false },
  slideUp: { duration: 0.5, repeat: false },
  wiggle:  { duration: 0.6, repeat: true },
};

/**
 * Legacy one-shot ENTRANCE kinds (`both` fill from a hidden or offset state). They are not gated for
 * SSR, so the static page plays them; on the screen it painted the client renders them final
 * (AMV-AC-036) — restarting the class would make the CTA vanish and play again.
 */
const LEGACY_ENTRANCE_KINDS = new Set(['scale', 'fadeIn', 'slideUp']);

function isLegacyOneShotEntrance(anim: AnimationConfig | null | undefined): boolean {
  const kind = anim?.kind as string | undefined;
  if (!kind || !LEGACY_ENTRANCE_KINDS.has(kind)) return false;
  return !(anim?.repeat ?? ANIM_DEFAULTS[kind]?.repeat ?? true);
}

/** Maps animation kind to CSS module class name */
const ANIM_CLASS_MAP: Record<string, string> = {
  pulse:   'buttonPulse',
  shimmer: 'buttonShimmer',
  bounce:  'buttonBounce',
  shake:   'buttonShake',
  glow:    'buttonGlow',
  ripple:  'buttonRipple',
  scale:   'buttonScale',
  fadeIn:  'buttonFadeIn',
  slideUp: 'buttonSlideUp',
  wiggle:  'buttonWiggle',
};

/** Compute intensity-based CSS custom properties per animation kind */
function getIntensityProps(kind: string, intensity: number): Record<string, string> {
  switch (kind) {
    case 'pulse':   return { '--anim-scale': String(1 + 0.02 + intensity * 0.13) };
    case 'shimmer': return { '--anim-glint-opacity': String(0.15 + intensity * 0.45) };
    case 'bounce':  return { '--anim-distance': `${5 + intensity * 20}px` };
    case 'shake':   return { '--anim-amplitude': `${3 + intensity * 9}px` };
    case 'glow':    return { '--anim-spread': `${4 + intensity * 16}px` };
    case 'scale':   return { '--anim-start-scale': String(0.85 - intensity * 0.35) };
    case 'slideUp': return { '--anim-slide-distance': `${10 + intensity * 40}px` };
    case 'wiggle':  return { '--anim-angle': `${3 + intensity * 12}deg` };
    default:        return {};
  }
}

function getAnimationStyles(anim: AnimationConfig | null | undefined): { className: string; cssVars: Record<string, string> } {
  if (!anim?.kind) return { className: '', cssVars: {} };
  const kind = anim.kind as string;
  const classKey = ANIM_CLASS_MAP[kind];
  const className = classKey ? (styles[classKey] ?? '') : '';
  const defaults = ANIM_DEFAULTS[kind] ?? { duration: 2, repeat: true };

  const cssVars: Record<string, string> = {
    '--anim-duration': `${anim.duration ?? defaults.duration}s`,
    '--anim-delay': `${anim.delay ?? 0}s`,
    '--anim-repeat': (anim.repeat ?? defaults.repeat) ? 'infinite' : '1',
  };
  if (anim.color) {
    cssVars['--anim-color'] = anim.color;
  }
  if (anim.intensity !== undefined) {
    Object.assign(cssVars, getIntensityProps(kind, anim.intensity));
  }
  return { className, cssVars };
}

/** Icon placement layout helpers */
function isVerticalPlacement(placement?: TapTargetIconPlacement): boolean {
  return placement === 'top' || placement === 'bottom';
}

function isFixedPlacement(placement?: TapTargetIconPlacement): boolean {
  return placement === 'fixedStart' || placement === 'fixedEnd';
}

export const Button = memo(function Button({
  tapTarget,
  locale,
  variables,
  step,
  onAction,
  disabled,
  isLoading = false,
  className,
  'data-testid': testId,
  editPath,
}: ButtonProps): React.ReactElement | null {
  const fontOverrides = useFontOverrides();
  // v2.17+ enter/idle/tap animations — take priority over legacy
  // appearance.animation (AnimationConfig) when present
  const { animProps, notifyTap, hasNewAnimations } = useTapTargetAnimation(tapTarget?.appearance);

  // Hooks stay ABOVE the early returns below — if tapTarget or the resolved
  // text materializes on a mounted button, an early return between hooks
  // flips the hook count ("Rendered more hooks than during the previous
  // render") and white-screens the funnel.
  // Animation: legacy AnimationConfig path, used only when no new-style
  // enter/idle/tap animation is configured (priority rule, schema 2.17)
  // …minus a one-shot entrance on the screen the static page painted (AMV-AC-036)
  const skipLegacyEntrance = useScreenAnimationContext()?.skipMountEntrances === true;
  const legacyAnim = useMemo(
    () => getAnimationStyles(
      skipLegacyEntrance && isLegacyOneShotEntrance(tapTarget?.appearance?.animation)
        ? undefined
        : tapTarget?.appearance?.animation,
    ),
    [tapTarget?.appearance?.animation, skipLegacyEntrance],
  );
  const action = tapTarget?.action;
  const handleClick = useCallback(() => {
    // Tap animation is fire-and-forget — it must never delay the action
    notifyTap();
    if (onAction && action) {
      onAction(createButtonClickEvent(action.kind, step, editPath));
    }
  }, [action, step, editPath, onAction, notifyTap]);

  if (!tapTarget) {
    return null;
  }

  const { content, appearance, layout, kind } = tapTarget;

  // Resolve content based on kind
  let buttonText: string | null = null;
  const isImageContent = kind === 'icon';

  if (!isImageContent && hasTranslations(content)) {
    buttonText = resolveLabel(content as LocalizedLabel, locale, variables);
  }

  // For label kind, return null if text cannot be resolved
  if (!isImageContent && buttonText === null) {
    return null;
  }

  // Icon support (only for kind="label")
  const hasIcon = !isImageContent && tapTarget.icon != null;
  const iconPlacement = tapTarget.iconPlacement ?? 'start';
  const iconInsets = tapTarget.iconInsets;

  // Map styles.
  // D-T9b-15: an authored fixed `height` becomes a MINIMUM. A two-line label inside a
  // 55px-tall button would otherwise be clipped by the same fixed height that used to be
  // safe when the label could only ever be one line.
  // `mapTapTargetStyles` only ever emits a numeric `height` (SizeSpec.height is a number and
  // the TapTarget mapper ignores heightPercentage), so this conversion is always px-safe.
  const { height: authoredHeight, ...mappedButtonStyles } = mapTapTargetStyles(appearance);
  const buttonStyles: CSSProperties = authoredHeight !== undefined
    ? { ...mappedButtonStyles, minHeight: authoredHeight }
    : mappedButtonStyles;
  const layoutStyles: CSSProperties = mapFrameLayoutStyles(layout);

  // FullWidth handling
  const isFullWidth = appearance?.fullWidth === true || (appearance?.fullWidth as unknown) === 'true';

  const wrapperStyles: CSSProperties = {
    ...layoutStyles,
    maxWidth: '100%',
    overflow: 'hidden',
    ...(isFullWidth && { alignSelf: 'stretch', width: '100%' }),
  };

  // Text styles from label content appearance
  const authoredTextStyles: CSSProperties = (!isImageContent && hasTranslations(content))
    ? mapTypographyStyles((content as LocalizedLabel).appearance, fontOverrides)
    : {};

  // WP8 UX M-4: a button with no ground of its own — a secondary action, a header close,
  // a header Back — sits directly on the screen's canvas, and with `appearance: {}` and no
  // authored label colour it inherits the engine default black. On a dark canvas that is an
  // invisible control. Fall back to the canvas's own readable foreground; a button that
  // paints its own background is left alone, because its label reads against THAT and not
  // against the canvas.
  const paintsOwnGround = Boolean(
    appearance?.backgroundColor || appearance?.gradient || appearance?.backgroundImage,
  );
  const canvasForegroundColor =
    !isImageContent && !paintsOwnGround && !authoredTextStyles.color
      ? resolveScreenForegroundColor(step as Parameters<typeof resolveScreenForegroundColor>[0])
      : undefined;

  const textStyles: CSSProperties = canvasForegroundColor
    ? { ...authoredTextStyles, color: canvasForegroundColor }
    : authoredTextStyles;

  // Extract textAlign for button justifyContent
  const textAlign = textStyles.textAlign;
  const justifyContent = textAlign === 'left' ? 'flex-start' : textAlign === 'right' ? 'flex-end' : 'center';

  // Visibility state
  const { hidden } = getButtonStateFromSchema(tapTarget, true, false);

  // Effective disabled state
  const isDisabled = disabled || isLoading;

  const animClassName = hasNewAnimations ? (animProps.className ?? '') : legacyAnim.className;
  const animCssVars = hasNewAnimations
    ? (animProps.style ?? {}) as Record<string, string>
    : legacyAnim.cssVars;

  // Border: use gradient fallback color if present
  // Normalize: editor path-setter may create object instead of array
  const rawBorderGradientColors = appearance?.borders?.gradient?.colors;
  const borderGradientColors = rawBorderGradientColors
    ? (Array.isArray(rawBorderGradientColors) ? rawBorderGradientColors : Object.values(rawBorderGradientColors) as string[])
    : [];
  const borderColor = borderGradientColors.length
    ? borderGradientColors[0]
    : (appearance?.borders?.color || 'transparent');

  const buttonStyle: CSSProperties = {
    ...buttonStyles,
    border: appearance?.borders
      ? `${appearance.borders.width ?? 0}px solid ${borderColor}`
      : 'none',
    cursor: isLoading ? 'wait' : (isDisabled ? 'not-allowed' : 'pointer'),
    justifyContent,
    ...(hidden && { display: 'none' }),
    // Icon layout: change flex-direction for vertical placements
    ...(hasIcon && isVerticalPlacement(iconPlacement as TapTargetIconPlacement) && { flexDirection: 'column' }),
    // Icon layout: relative positioning for fixedStart/fixedEnd
    ...(hasIcon && isFixedPlacement(iconPlacement as TapTargetIconPlacement) && { position: 'relative' as const }),
    // Animation CSS custom properties
    ...animCssVars,
  };

  const schemaTestId = testId ?? getSchemaTestId(editPath);

  // Build class names
  const classNames = [
    styles.button,
    isDisabled ? styles.buttonDisabled : '',
    isLoading ? styles.buttonLoading : '',
    animClassName,
    className ?? '',
  ].filter(Boolean).join(' ');

  // Render icon element
  const renderIcon = () => {
    if (!hasIcon || !tapTarget.icon) return null;
    const iconStyle: CSSProperties = {
      ...mapEdgeInsetsToCSS(iconInsets),
      flexShrink: 0,
      ...(isFixedPlacement(iconPlacement as TapTargetIconPlacement) && {
        position: 'absolute' as const,
        top: '50%',
        transform: 'translateY(-50%)',
        ...(iconPlacement === 'fixedStart' && { left: iconInsets?.start ?? 16 }),
        ...(iconPlacement === 'fixedEnd' && { right: iconInsets?.end ?? 16 }),
      }),
    };
    return (
      <span style={iconStyle} className={styles.buttonIcon}>
        <Image
          image={tapTarget.icon as LocalizedImage}
          locale={locale}
          data-testid={schemaTestId ? `${schemaTestId}.icon` : undefined}
        />
      </span>
    );
  };

  // Render text element.
  // D-T9b-15: the wrapping rules live in the CSS module, not in an inline style — an
  // inline `white-space: nowrap` could not be overridden by any stylesheet, which is why
  // the label was ellipsised on 15 screens.
  const renderText = () => (
    <span className={styles.buttonLabel} style={textStyles}>{buttonText}</span>
  );

  // Determine icon render order
  const iconBefore = iconPlacement === 'start' || iconPlacement === 'fixedStart' || iconPlacement === 'top';

  return (
    <div
      style={wrapperStyles}
      data-edit-path={editPath}
    >
      <button
        type="button"
        className={classNames}
        style={buttonStyle}
        onClick={isDisabled ? undefined : handleClick}
        disabled={isDisabled}
        aria-disabled={isDisabled}
        aria-busy={isLoading}
        data-testid={schemaTestId}
      >
        {isLoading && <span className={styles.spinner} style={{ color: textStyles.color }} aria-hidden="true" />}
        {isImageContent ? (
          <Image
            image={content as LocalizedImage}
            locale={locale}
            data-testid={schemaTestId ? `${schemaTestId}.icon` : undefined}
          />
        ) : (
          <>
            {hasIcon && iconBefore && renderIcon()}
            {renderText()}
            {hasIcon && !iconBefore && renderIcon()}
          </>
        )}
      </button>
    </div>
  );
});

Button.displayName = 'Button';
